import { useState, useCallback } from "react";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import { normalizeBodyRegion } from "@/lib/body-region-utils";

// Helper to check if RPC function doesn't exist
// Handles both PostgreSQL error 42883 and Supabase schema cache miss
function isRpcNotFoundError(error: { code?: string; message?: string }): boolean {
  return error.code === '42883' ||
    error.code === 'PGRST202' ||
    error.message?.includes('Could not find the function') ||
    (error.message?.includes('function') && error.message?.includes('does not exist')) ||
    false;
}

// Template list item type
export interface TemplateListItem {
  exam_family_id: string;
  display_name: string;
  modality: string;
  body_region: string;
  is_active: boolean;
}

// Section customization types
export interface SectionConfig {
  section_token: string;
  section_title: string;
  order_index: number;
}

// Full template detail for editor
export interface TemplateExamInfo {
  exam_family_id: string;
  display_name: string;
  modality: string;
  body_region: string;
  contrast_phase: string | null;
  is_active: boolean;
  default_sections: SectionConfig[] | null;
  custom_sections: SectionConfig[] | null;
}

export interface TemplateTechnique {
  technique_set_id: string;
  technique_text: string;
}

export interface TemplateSection {
  section_token: string;
  section_title: string;
  default_text: string;
  order_index: number;
}

export interface TemplateStructure {
  structure_key: string;
  display_name: string;
  section_token?: string;
  body_system?: string;
}

export interface TemplateDetail {
  exam: TemplateExamInfo;
  technique: TemplateTechnique | null;
  sections: TemplateSection[];
  required_structures: TemplateStructure[];
  optional_structures: TemplateStructure[];
}

// Templates grouped by modality within a body region
export interface ModalityGroup {
  [modality: string]: TemplateListItem[];
}

// Grouped templates by body region, then by modality
export interface GroupedTemplates {
  [bodyRegion: string]: ModalityGroup;
}

// Modality display order
export const MODALITY_ORDER = ['CT', 'MR', 'XR', 'US', 'NM', 'MG', 'FL', 'Other'];

interface UseTemplateListResult {
  templates: TemplateListItem[];
  grouped: GroupedTemplates;
  loading: boolean;
  fetch: (search?: string) => Promise<void>;
}

interface UseTemplateDetailResult {
  detail: TemplateDetail | null;
  loading: boolean;
  fetch: (examFamilyId: string) => Promise<void>;
  clear: () => void;
}

interface UseUpdateTemplateResult {
  updating: boolean;
  updateExam: (examFamilyId: string, displayName: string, isActive: boolean) => Promise<boolean>;
  updateTechnique: (examFamilyId: string, techniqueText: string) => Promise<boolean>;
  updateSection: (examFamilyId: string, sectionToken: string, defaultText: string) => Promise<boolean>;
  updateSectionTitle: (examFamilyId: string, sectionToken: string, newTitle: string) => Promise<boolean>;
  addSection: (examFamilyId: string, sectionToken: string, sectionTitle: string, orderIndex: number) => Promise<boolean>;
  removeSection: (examFamilyId: string, sectionToken: string) => Promise<boolean>;
  reorderSections: (examFamilyId: string, orderedTokens: string[]) => Promise<boolean>;
  toggleStructure: (examFamilyId: string, sectionToken: string, structureKey: string, include: boolean) => Promise<boolean>;
  updateEnabledSections: (examFamilyId: string, sections: SectionConfig[]) => Promise<boolean>;
  restoreDefaultSections: (examFamilyId: string) => Promise<boolean>;
  syncDefaultSections: (examFamilyId: string) => Promise<boolean>;
}

interface UseModalitySectionsResult {
  sections: SectionConfig[];
  loading: boolean;
  fetch: (modality: string) => Promise<void>;
}

interface UseCreateTemplateResult {
  creating: boolean;
  create: (examFamilyId: string, displayName: string, modality: string, bodyRegion: string, contrastPhase: string) => Promise<boolean>;
}

/**
 * Hook for fetching the template list
 *
 * Note: This attempts to use the admin_list_templates RPC. If it doesn't exist,
 * falls back to direct table access on exam_family_registry.
 */
export function useTemplateList(): UseTemplateListResult {
  const [templates, setTemplates] = useState<TemplateListItem[]>([]);
  const [grouped, setGrouped] = useState<GroupedTemplates>({});
  const [loading, setLoading] = useState(false);

  const fetch = useCallback(async (search?: string) => {
    setLoading(true);
    try {
      // Try RPC first
      const { data: rpcData, error: rpcError } = await supabase.rpc('admin_list_templates', {
        p_search: search || null
      });

      if (rpcError) {
        // If RPC doesn't exist, fall back to direct table access
        if (isRpcNotFoundError(rpcError)) {
          console.warn('admin_list_templates RPC not found - falling back to direct table access', rpcError.code);

          let query = supabase
            .from('exam_family_registry')
            .select('exam_family_id, display_name, modality, body_region, is_active')
            .order('modality')
            .order('display_name');

          if (search) {
            query = query.or(`display_name.ilike.%${search}%,exam_family_id.ilike.%${search}%`);
          }

          const { data: tableData, error: tableError } = await query;

          if (tableError) {
            console.error('Direct table access failed:', tableError.message, tableError.code, tableError.details);
            throw tableError;
          }

          console.log(`Loaded ${(tableData || []).length} templates via direct table access`);
          const items = (tableData || []) as TemplateListItem[];
          setTemplates(items);
          setGrouped(groupByBodyRegion(items));
          return;
        }
        throw rpcError;
      }

      // Parse RPC result
      const result = rpcData as unknown as {
        success: boolean;
        templates: TemplateListItem[];
        error?: string;
      };

      if (!result.success) {
        throw new Error(result.error || 'Failed to fetch templates');
      }

      const items = result.templates || [];
      setTemplates(items);
      setGrouped(groupByBodyRegion(items));
    } catch (err) {
      const error = err as { message?: string; code?: string; details?: string };
      console.error('Failed to fetch templates:', error.message, error.code, error.details);
      toast.error(`Failed to load templates: ${error.message || 'Unknown error'}`);
      setTemplates([]);
      setGrouped({});
    } finally {
      setLoading(false);
    }
  }, []);

  return {
    templates,
    grouped,
    loading,
    fetch
  };
}

/**
 * Hook for fetching full template detail for the editor
 */
export function useTemplateDetail(): UseTemplateDetailResult {
  const [detail, setDetail] = useState<TemplateDetail | null>(null);
  const [loading, setLoading] = useState(false);

  const fetch = useCallback(async (examFamilyId: string) => {
    setLoading(true);
    try {
      // Try RPC first
      const { data: rpcData, error: rpcError } = await supabase.rpc('admin_get_template_detail', {
        p_exam_family_id: examFamilyId
      });

      if (rpcError) {
        // If RPC doesn't exist, build from multiple table queries
        if (isRpcNotFoundError(rpcError)) {
          console.warn('admin_get_template_detail RPC not found - falling back to direct table access');

          // Fetch exam info
          const { data: examData, error: examError } = await supabase
            .from('exam_family_registry')
            .select('*')
            .eq('exam_family_id', examFamilyId)
            .single();

          if (examError) throw examError;

          // Fetch technique using technique_set_id from exam
          let techniqueData = null;
          if (examData.technique_set_id) {
            const { data } = await supabase
              .from('technique_set_registry')
              .select('*')
              .eq('technique_set_id', examData.technique_set_id)
              .maybeSingle();
            techniqueData = data;
          }

          // Fetch sections
          const { data: sectionsData } = await supabase
            .from('template_section_registry')
            .select('section_token, section_title, default_text, order_index')
            .eq('exam_family_id', examFamilyId)
            .eq('is_active', true)
            .order('order_index');

          // Fetch required structures via section_structure_map
          const { data: structureMapData } = await supabase
            .from('section_structure_map')
            .select('structure_key, section_token')
            .eq('exam_family_id', examFamilyId)
            .eq('is_active', true);

          // Fetch structure details
          const { data: structuresData } = await supabase
            .from('structure_registry')
            .select('structure_key, display_name, body_system');

          // Build the detail object
          const structureMap = new Map(
            (structuresData || []).map(s => [s.structure_key, s])
          );

          const requiredStructureKeys = new Set(
            (structureMapData || []).map(m => m.structure_key)
          );

          const requiredStructures = (structureMapData || []).map(m => ({
            structure_key: m.structure_key,
            display_name: structureMap.get(m.structure_key)?.display_name || m.structure_key,
            section_token: m.section_token,
            body_system: structureMap.get(m.structure_key)?.body_system
          }));

          // Optional structures: same body system but not required
          const examBodyRegion = examData.body_region?.toLowerCase() || '';
          const optionalStructures = (structuresData || [])
            .filter(s => {
              const bodySystem = (s.body_system || '').toLowerCase();
              return bodySystem.includes(examBodyRegion) || examBodyRegion.includes(bodySystem);
            })
            .filter(s => !requiredStructureKeys.has(s.structure_key))
            .map(s => ({
              structure_key: s.structure_key,
              display_name: s.display_name,
              body_system: s.body_system
            }));

          // Parse default_sections and custom_sections if they exist
          const defaultSections = examData.default_sections
            ? (examData.default_sections as SectionConfig[])
            : null;
          const customSections = examData.custom_sections
            ? (examData.custom_sections as SectionConfig[])
            : null;

          setDetail({
            exam: {
              exam_family_id: examData.exam_family_id,
              display_name: examData.display_name,
              modality: examData.modality,
              body_region: examData.body_region,
              contrast_phase: examData.contrast_phase,
              is_active: examData.is_active,
              default_sections: defaultSections,
              custom_sections: customSections
            },
            technique: techniqueData ? {
              technique_set_id: techniqueData.technique_set_id,
              technique_text: techniqueData.technique_text || ''
            } : null,
            sections: (sectionsData || []).map(s => ({
              section_token: s.section_token,
              section_title: s.section_title,
              default_text: s.default_text || '',
              order_index: s.order_index
            })),
            required_structures: requiredStructures,
            optional_structures: optionalStructures
          });
          return;
        }
        throw rpcError;
      }

      // Parse RPC result
      const result = rpcData as unknown as {
        success: boolean;
        data: TemplateDetail;
        error?: string;
      };

      if (!result.success) {
        throw new Error(result.error || 'Failed to fetch template detail');
      }

      setDetail(result.data);
    } catch (err) {
      console.error('Failed to fetch template detail:', err);
      toast.error('Failed to load template details');
      setDetail(null);
    } finally {
      setLoading(false);
    }
  }, []);

  const clear = useCallback(() => {
    setDetail(null);
  }, []);

  return {
    detail,
    loading,
    fetch,
    clear
  };
}

/**
 * Hook for template mutations
 */
export function useUpdateTemplate(): UseUpdateTemplateResult {
  const [updating, setUpdating] = useState(false);

  const updateExam = useCallback(async (
    examFamilyId: string,
    displayName: string,
    isActive: boolean
  ): Promise<boolean> => {
    setUpdating(true);
    try {
      // Try RPC first
      const { data: rpcData, error: rpcError } = await supabase.rpc('admin_update_template_exam', {
        p_exam_family_id: examFamilyId,
        p_display_name: displayName,
        p_is_active: isActive
      });

      if (rpcError) {
        // Fall back to direct update
        if (isRpcNotFoundError(rpcError)) {
          const { error: updateError } = await supabase
            .from('exam_family_registry')
            .update({
              display_name: displayName,
              is_active: isActive,
              updated_at: new Date().toISOString()
            })
            .eq('exam_family_id', examFamilyId);

          if (updateError) throw updateError;
          toast.success('Template updated');
          return true;
        }
        throw rpcError;
      }

      const result = rpcData as { success: boolean; error?: string };
      if (!result.success) {
        throw new Error(result.error || 'Failed to update template');
      }

      toast.success('Template updated');
      return true;
    } catch (err) {
      console.error('Failed to update template:', err);
      toast.error('Failed to update template');
      return false;
    } finally {
      setUpdating(false);
    }
  }, []);

  const updateTechnique = useCallback(async (
    examFamilyId: string,
    techniqueText: string
  ): Promise<boolean> => {
    setUpdating(true);
    try {
      const { data: rpcData, error: rpcError } = await supabase.rpc('admin_update_template_technique', {
        p_exam_family_id: examFamilyId,
        p_technique_text: techniqueText
      });

      if (rpcError) {
        if (isRpcNotFoundError(rpcError)) {
          // First, get the technique_set_id from exam_family_registry
          const { data: examData, error: examError } = await supabase
            .from('exam_family_registry')
            .select('technique_set_id')
            .eq('exam_family_id', examFamilyId)
            .single();

          if (examError) throw examError;
          if (!examData?.technique_set_id) {
            throw new Error('No technique set associated with this template');
          }

          // Then update technique_set_registry by technique_set_id
          const { error: updateError } = await supabase
            .from('technique_set_registry')
            .update({
              technique_text: techniqueText,
              updated_at: new Date().toISOString()
            })
            .eq('technique_set_id', examData.technique_set_id);

          if (updateError) throw updateError;
          toast.success('Technique updated');
          return true;
        }
        throw rpcError;
      }

      const result = rpcData as { success: boolean; error?: string };
      if (!result.success) {
        throw new Error(result.error || 'Failed to update technique');
      }

      toast.success('Technique updated');
      return true;
    } catch (err) {
      console.error('Failed to update technique:', err);
      toast.error('Failed to update technique');
      return false;
    } finally {
      setUpdating(false);
    }
  }, []);

  const updateSection = useCallback(async (
    examFamilyId: string,
    sectionToken: string,
    defaultText: string
  ): Promise<boolean> => {
    setUpdating(true);
    try {
      const { data: rpcData, error: rpcError } = await supabase.rpc('admin_update_template_section', {
        p_exam_family_id: examFamilyId,
        p_section_token: sectionToken,
        p_default_text: defaultText
      });

      if (rpcError) {
        if (isRpcNotFoundError(rpcError)) {
          const { error: updateError } = await supabase
            .from('template_section_registry')
            .update({
              default_text: defaultText,
              updated_at: new Date().toISOString()
            })
            .eq('exam_family_id', examFamilyId)
            .eq('section_token', sectionToken);

          if (updateError) throw updateError;
          toast.success('Section updated');
          return true;
        }
        throw rpcError;
      }

      const result = rpcData as { success: boolean; error?: string };
      if (!result.success) {
        throw new Error(result.error || 'Failed to update section');
      }

      toast.success('Section updated');
      return true;
    } catch (err) {
      console.error('Failed to update section:', err);
      toast.error('Failed to update section');
      return false;
    } finally {
      setUpdating(false);
    }
  }, []);

  const updateSectionTitle = useCallback(async (
    examFamilyId: string,
    sectionToken: string,
    newTitle: string
  ): Promise<boolean> => {
    setUpdating(true);
    try {
      const { error } = await supabase
        .from('template_section_registry')
        .update({
          section_title: newTitle,
          updated_at: new Date().toISOString()
        })
        .eq('exam_family_id', examFamilyId)
        .eq('section_token', sectionToken);

      if (error) throw error;
      toast.success('Section title updated');
      return true;
    } catch (err) {
      console.error('Failed to update section title:', err);
      toast.error('Failed to update section title');
      return false;
    } finally {
      setUpdating(false);
    }
  }, []);

  const reorderSections = useCallback(async (
    examFamilyId: string,
    orderedTokens: string[]
  ): Promise<boolean> => {
    setUpdating(true);
    try {
      // Bulk-update order_index for each token based on position in array
      for (let i = 0; i < orderedTokens.length; i++) {
        const { error } = await supabase
          .from('template_section_registry')
          .update({
            order_index: i,
            updated_at: new Date().toISOString()
          })
          .eq('exam_family_id', examFamilyId)
          .eq('section_token', orderedTokens[i]);

        if (error) throw error;
      }
      toast.success('Section order updated');
      return true;
    } catch (err) {
      console.error('Failed to reorder sections:', err);
      toast.error('Failed to reorder sections');
      return false;
    } finally {
      setUpdating(false);
    }
  }, []);

  const syncDefaultSections = useCallback(async (
    examFamilyId: string
  ): Promise<boolean> => {
    try {
      // Fetch all active template_section_registry rows for this exam
      const { data: activeSections, error: fetchError } = await supabase
        .from('template_section_registry')
        .select('section_token, section_title, order_index')
        .eq('exam_family_id', examFamilyId)
        .eq('is_active', true)
        .order('order_index');

      if (fetchError) throw fetchError;

      const defaultSections: SectionConfig[] = (activeSections ?? []).map(s => ({
        section_token: s.section_token,
        section_title: s.section_title,
        order_index: s.order_index
      }));

      // Write back to exam_family_registry.default_sections
      const { error: updateError } = await supabase
        .from('exam_family_registry')
        .update({
          default_sections: defaultSections,
          updated_at: new Date().toISOString()
        })
        .eq('exam_family_id', examFamilyId);

      if (updateError) throw updateError;
      return true;
    } catch (err) {
      console.error('Failed to sync default sections:', err);
      toast.error('Failed to sync default sections');
      return false;
    }
  }, []);

  const addSection = useCallback(async (
    examFamilyId: string,
    sectionToken: string,
    sectionTitle: string,
    orderIndex: number
  ): Promise<boolean> => {
    setUpdating(true);
    try {
      const { data: rpcData, error: rpcError } = await supabase.rpc('admin_add_template_section', {
        p_exam_family_id: examFamilyId,
        p_section_token: sectionToken,
        p_section_title: sectionTitle,
        p_order_index: orderIndex
      });

      if (rpcError) {
        if (isRpcNotFoundError(rpcError)) {
          const { error: insertError } = await supabase
            .from('template_section_registry')
            .insert({
              exam_family_id: examFamilyId,
              section_token: sectionToken,
              section_title: sectionTitle,
              order_index: orderIndex,
              default_text: '',
              is_active: true
            });

          if (insertError) throw insertError;
          toast.success('Section added');
          return true;
        }
        throw rpcError;
      }

      const result = rpcData as { success: boolean; error?: string };
      if (!result.success) {
        throw new Error(result.error || 'Failed to add section');
      }

      toast.success('Section added');
      return true;
    } catch (err) {
      console.error('Failed to add section:', err);
      toast.error('Failed to add section');
      return false;
    } finally {
      setUpdating(false);
    }
  }, []);

  const removeSection = useCallback(async (
    examFamilyId: string,
    sectionToken: string
  ): Promise<boolean> => {
    setUpdating(true);
    try {
      const { data: rpcData, error: rpcError } = await supabase.rpc('admin_remove_template_section', {
        p_exam_family_id: examFamilyId,
        p_section_token: sectionToken
      });

      if (rpcError) {
        if (isRpcNotFoundError(rpcError)) {
          const { error: deleteError } = await supabase
            .from('template_section_registry')
            .delete()
            .eq('exam_family_id', examFamilyId)
            .eq('section_token', sectionToken);

          if (deleteError) throw deleteError;
          toast.success('Section removed');
          return true;
        }
        throw rpcError;
      }

      const result = rpcData as { success: boolean; error?: string };
      if (!result.success) {
        throw new Error(result.error || 'Failed to remove section');
      }

      toast.success('Section removed');
      return true;
    } catch (err) {
      console.error('Failed to remove section:', err);
      toast.error('Failed to remove section');
      return false;
    } finally {
      setUpdating(false);
    }
  }, []);

  const toggleStructure = useCallback(async (
    examFamilyId: string,
    sectionToken: string,
    structureKey: string,
    include: boolean
  ): Promise<boolean> => {
    setUpdating(true);
    try {
      const { data: rpcData, error: rpcError } = await supabase.rpc('admin_toggle_template_structure', {
        p_exam_family_id: examFamilyId,
        p_section_token: sectionToken,
        p_structure_key: structureKey,
        p_include: include
      });

      if (rpcError) {
        if (isRpcNotFoundError(rpcError)) {
          if (include) {
            const { error: insertError } = await supabase
              .from('section_structure_map')
              .insert({
                exam_family_id: examFamilyId,
                section_token: sectionToken,
                structure_key: structureKey
              });
            if (insertError) throw insertError;
          } else {
            const { error: deleteError } = await supabase
              .from('section_structure_map')
              .delete()
              .eq('exam_family_id', examFamilyId)
              .eq('structure_key', structureKey);
            if (deleteError) throw deleteError;
          }
          toast.success(include ? 'Structure added' : 'Structure removed');
          return true;
        }
        throw rpcError;
      }

      const result = rpcData as { success: boolean; error?: string };
      if (!result.success) {
        throw new Error(result.error || 'Failed to toggle structure');
      }

      toast.success(include ? 'Structure added' : 'Structure removed');
      return true;
    } catch (err) {
      console.error('Failed to toggle structure:', err);
      toast.error('Failed to update structure');
      return false;
    } finally {
      setUpdating(false);
    }
  }, []);

  const updateEnabledSections = useCallback(async (
    examFamilyId: string,
    sections: SectionConfig[]
  ): Promise<boolean> => {
    setUpdating(true);
    try {
      const { error } = await supabase
        .from('exam_family_registry')
        .update({
          custom_sections: sections,
          updated_at: new Date().toISOString()
        })
        .eq('exam_family_id', examFamilyId);

      if (error) throw error;
      toast.success('Section headings updated');
      return true;
    } catch (err) {
      console.error('Failed to update enabled sections:', err);
      toast.error('Failed to update section headings');
      return false;
    } finally {
      setUpdating(false);
    }
  }, []);

  const restoreDefaultSections = useCallback(async (
    examFamilyId: string
  ): Promise<boolean> => {
    setUpdating(true);
    try {
      const { error } = await supabase
        .from('exam_family_registry')
        .update({
          custom_sections: null,
          updated_at: new Date().toISOString()
        })
        .eq('exam_family_id', examFamilyId);

      if (error) throw error;
      toast.success('Section headings restored to default');
      return true;
    } catch (err) {
      console.error('Failed to restore default sections:', err);
      toast.error('Failed to restore default section headings');
      return false;
    } finally {
      setUpdating(false);
    }
  }, []);

  return {
    updating,
    updateExam,
    updateTechnique,
    updateSection,
    updateSectionTitle,
    addSection,
    removeSection,
    reorderSections,
    toggleStructure,
    updateEnabledSections,
    restoreDefaultSections,
    syncDefaultSections
  };
}

/**
 * Hook for creating new templates
 */
export function useCreateTemplate(): UseCreateTemplateResult {
  const [creating, setCreating] = useState(false);

  const create = useCallback(async (
    examFamilyId: string,
    displayName: string,
    modality: string,
    bodyRegion: string,
    contrastPhase: string
  ): Promise<boolean> => {
    setCreating(true);
    try {
      const { data: rpcData, error: rpcError } = await supabase.rpc('admin_create_template', {
        p_exam_family_id: examFamilyId,
        p_display_name: displayName,
        p_modality: modality,
        p_body_region: bodyRegion,
        p_contrast_phase: contrastPhase
      });

      if (rpcError) {
        if (isRpcNotFoundError(rpcError)) {
          // Direct insert - use examFamilyId as template_family (default pattern)
          const { error: insertError } = await supabase
            .from('exam_family_registry')
            .insert({
              exam_family_id: examFamilyId,
              display_name: displayName,
              modality: modality,
              body_region: bodyRegion,
              contrast_phase: contrastPhase || null,
              template_family: examFamilyId, // Required field - default to exam_family_id
              is_active: true
            });

          if (insertError) {
            console.error('Template insert failed:', insertError.message, insertError.code, insertError.details);
            throw insertError;
          }
          console.log(`Template created: ${examFamilyId}`);
          toast.success('Template created');
          return true;
        }
        throw rpcError;
      }

      const result = rpcData as { success: boolean; error?: string };
      if (!result.success) {
        throw new Error(result.error || 'Failed to create template');
      }

      toast.success('Template created');
      return true;
    } catch (err) {
      const error = err as { message?: string; code?: string; details?: string };
      console.error('Failed to create template:', error.message, error.code, error.details);
      toast.error(`Failed to create template: ${error.message || 'Unknown error'}`);
      return false;
    } finally {
      setCreating(false);
    }
  }, []);

  return {
    creating,
    create
  };
}

/**
 * Hook for fetching available sections from modality_section_pool
 * These are all sections available for a given modality, used for customization
 */
export function useModalitySections(): UseModalitySectionsResult {
  const [sections, setSections] = useState<SectionConfig[]>([]);
  const [loading, setLoading] = useState(false);

  const fetch = useCallback(async (modality: string) => {
    setLoading(true);
    try {
      const { data, error } = await supabase
        .from('modality_section_pool')
        .select('section_token, section_title, order_index')
        .eq('modality', modality)
        .eq('is_active', true)
        .order('order_index');

      if (error) throw error;

      const sectionConfigs: SectionConfig[] = (data || []).map(s => ({
        section_token: s.section_token,
        section_title: s.section_title,
        order_index: s.order_index
      }));

      setSections(sectionConfigs);
    } catch (err) {
      console.error('Failed to fetch modality sections:', err);
      toast.error('Failed to load available sections');
      setSections([]);
    } finally {
      setLoading(false);
    }
  }, []);

  return {
    sections,
    loading,
    fetch
  };
}

// Helper function to group templates by body region
// Helper function to group templates by body region, then by modality
function groupByBodyRegion(templates: TemplateListItem[]): GroupedTemplates {
  return templates.reduce((acc, template) => {
    const bodyRegion = normalizeBodyRegion(template.body_region, template.modality);
    const modality = template.modality || 'Other';

    if (!acc[bodyRegion]) {
      acc[bodyRegion] = {};
    }
    if (!acc[bodyRegion][modality]) {
      acc[bodyRegion][modality] = [];
    }
    acc[bodyRegion][modality].push(template);
    return acc;
  }, {} as GroupedTemplates);
}
